/**
 * Deterministic thresholds for Schedule Health and Schedule Quality.
 * Tune here without touching UI components.
 */

export const HEALTH_THRESHOLDS = {
  /** Negative float count: OK=0, Warning 1-5, Critical >5 */
  negativeFloat: { warnMax: 5, criticalMin: 6 },
  /** Critical/near-critical (float ≤5d): OK ≤10, Warning 11-25, Critical >25 */
  criticalNearCritical: { okMax: 10, warnMax: 25 },
  /** High-float % >44d: OK ≤15%, Warning 16-30%, Critical >30% */
  highFloatPct: { okMax: 15, warnMax: 30 },
  /** Project finish delta (days late): OK ≤0, Warning 1-14, Critical >14 */
  finishDeltaDays: { warnMax: 14, criticalMin: 15 },
  /** Min total float (days): OK ≥0, Warning (-7,0), Critical ≤-7 */
  minFloatDays: { warnMin: -7, criticalMax: -8 },
} as const;

export const QUALITY_THRESHOLDS = {
  /** High float % >44d: Pass ≤15%, Warn 16-30%, Fail >30% */
  highFloatPct: { passMax: 15, warnMax: 30 },
  /** Long duration % (activities >44d): Pass ≤10%, Warn 11-20%, Fail >20% */
  longDurationPct: { passMax: 10, warnMax: 20, longDays: 44 },
  /** Missing predecessors/successors: Pass=0, Warn 1-3, Fail >3 */
  missingRelations: { warnMax: 3, failMin: 4 },
  /** Constraints count (informational or Pass ≤N) */
  constraintsCount: { passMax: 10 },
  /** Excessive lags */
  excessiveLags: { passMax: 5 },
} as const;

/** 4-state status: Unknown when value is missing, fallback, or invalid */
export type HealthStatus = 'ok' | 'warning' | 'critical' | 'unknown';
export type QualityStatus = 'pass' | 'warn' | 'fail';

/**
 * Returns 'unknown' when value is null/undefined, or when isFallback/!hasValue.
 * Otherwise returns OK/Warning/Critical from thresholds.
 */
export function resolveHealthStatus(
  metric: keyof typeof HEALTH_THRESHOLDS,
  value: number | null | undefined,
  options?: { isFallback?: boolean; hasValue?: boolean }
): HealthStatus {
  if (options?.isFallback === true || options?.hasValue === false) return 'unknown';
  if (value == null || (typeof value === 'number' && isNaN(value))) return 'unknown';
  return getHealthStatus(metric, value);
}

export function getHealthStatus(
  metric: keyof typeof HEALTH_THRESHOLDS,
  value: number
): HealthStatus {
  switch (metric) {
    case 'negativeFloat':
      if (value === 0) return 'ok';
      if (value <= HEALTH_THRESHOLDS.negativeFloat.warnMax) return 'warning';
      return 'critical';
    case 'criticalNearCritical':
      if (value <= HEALTH_THRESHOLDS.criticalNearCritical.okMax) return 'ok';
      if (value <= HEALTH_THRESHOLDS.criticalNearCritical.warnMax) return 'warning';
      return 'critical';
    case 'highFloatPct':
      if (value <= HEALTH_THRESHOLDS.highFloatPct.okMax) return 'ok';
      if (value <= HEALTH_THRESHOLDS.highFloatPct.warnMax) return 'warning';
      return 'critical';
    case 'finishDeltaDays':
      if (value <= 0) return 'ok';
      if (value < HEALTH_THRESHOLDS.finishDeltaDays.criticalMin) return 'warning';
      return 'critical';
    case 'minFloatDays':
      if (value >= 0) return 'ok';
      if (value > HEALTH_THRESHOLDS.minFloatDays.criticalMax) return 'warning';
      return 'critical';
    default:
      return 'ok';
  }
}

export function getQualityStatus(
  check: keyof typeof QUALITY_THRESHOLDS,
  value: number
): QualityStatus {
  switch (check) {
    case 'highFloatPct':
      if (value <= QUALITY_THRESHOLDS.highFloatPct.passMax) return 'pass';
      if (value <= QUALITY_THRESHOLDS.highFloatPct.warnMax) return 'warn';
      return 'fail';
    case 'longDurationPct':
      if (value <= QUALITY_THRESHOLDS.longDurationPct.passMax) return 'pass';
      if (value <= QUALITY_THRESHOLDS.longDurationPct.warnMax) return 'warn';
      return 'fail';
    case 'missingRelations':
      if (value === 0) return 'pass';
      if (value <= QUALITY_THRESHOLDS.missingRelations.warnMax) return 'warn';
      return 'fail';
    case 'constraintsCount':
      return value <= QUALITY_THRESHOLDS.constraintsCount.passMax ? 'pass' : 'warn';
    case 'excessiveLags':
      return value <= QUALITY_THRESHOLDS.excessiveLags.passMax ? 'pass' : 'warn';
    default:
      return 'pass';
  }
}
